import classNames from 'classnames';
import { FC, useCallback, useContext, useEffect, useState } from 'react';

import { Button, ButtonSizes, ButtonVariant, Icon, Typography } from '@/shared/ui';

import AccessibilityContext from '@/contexts/AccessibilityContext';

import './AccessibilityPanelStyles.scss';

interface Props {
  isPanelShown: boolean;
}

export enum AccessibilityFontSize {
  DEFAULT = '',
  LARGE_1 = 'large-1',
  LARGE_2 = 'large-2',
  LARGE_3 = 'large-3',
}

export const FONT_SIZES = [
  AccessibilityFontSize.DEFAULT,
  AccessibilityFontSize.LARGE_1,
  AccessibilityFontSize.LARGE_2,
  AccessibilityFontSize.LARGE_3,
];

export const AccessibilityPanel: FC<Props> = ({ isPanelShown }) => {
  const { turnOnAccessibilityPanel } = useContext(AccessibilityContext);

  const [fontSize, setFontSize] = useState<AccessibilityFontSize>(
    () => (localStorage.getItem('accessibilitySize') as AccessibilityFontSize) || AccessibilityFontSize.DEFAULT,
  );
  const [isContrast, setContrast] = useState<boolean>(() => localStorage.getItem('accessibilityContrast') === 'true');

  useEffect(() => {
    const htmlTag = document.documentElement;

    htmlTag.classList.remove('accessibility-size', ...FONT_SIZES.filter(Boolean));

    if (fontSize) {
      htmlTag.classList.add('accessibility-size', fontSize);
      localStorage.setItem('accessibilitySize', fontSize);
    } else {
      localStorage.removeItem('accessibilitySize');
    }

    if (isContrast) {
      htmlTag.classList.add('contrastColors');
      localStorage.setItem('accessibilityContrast', 'true');
    } else {
      htmlTag.classList.remove('contrastColors');
      localStorage.removeItem('accessibilityContrast');
    }
  }, [fontSize, isContrast]);

  const increaseFontSize = useCallback(() => {
    const index = FONT_SIZES.indexOf(fontSize);
    if (index < FONT_SIZES.length - 1) {
      setFontSize(FONT_SIZES[index + 1]);
    }
  }, [fontSize]);

  const decreaseFontSize = useCallback(() => {
    const index = FONT_SIZES.indexOf(fontSize);
    if (index > 0) {
      setFontSize(FONT_SIZES[index - 1]);
    }
  }, [fontSize]);

  const toggleContrast = useCallback(() => {
    setContrast((prev) => !prev);
  }, []);

  const resetAccessibility = useCallback(() => {
    setFontSize(AccessibilityFontSize.DEFAULT);
    setContrast(false);
  }, []);

  return (
    <div className={classNames('banner-accessibility-panel', { 'banner-accessibility-panel--opened': isPanelShown })}>
      <div className="container">
        <div className="banner-accessibility-panel__container-inner">
          <div className="banner-accessibility-panel__settings-filter">
            <Typography variant="TEXT16" color="white" className="banner-accessibility-panel__settings-title">
              Контраст
            </Typography>
            <Button
              onClick={toggleContrast}
              variant={ButtonVariant.ICON}
              size={ButtonSizes.LG}
              className="banner-accessibility-panel__button"
              aria-label="Contrast mode"
              data-testid="accessibility-panel-contrast"
            >
              <Icon name="contrast" width={24} height={24} />
            </Button>
          </div>
          <div className="banner-accessibility-panel__font-settings">
            <Typography variant="TEXT16" color="white" className="banner-accessibility-panel__settings-title">
              Розмір шрифта
            </Typography>
            <Button
              variant={ButtonVariant.ICON}
              size={ButtonSizes.LG}
              onClick={decreaseFontSize}
              className="banner-accessibility-panel__button"
              aria-label="Decrease font size"
              data-testid="accessibility-panel-decrease"
            >
              <Icon name="minus" width={24} height={24} />
            </Button>
            <Button
              variant={ButtonVariant.ICON}
              size={ButtonSizes.LG}
              onClick={increaseFontSize}
              className="banner-accessibility-panel__button"
              aria-label="Increase font size"
              data-testid="accessibility-panel-increase"
            >
              <Icon name="plus" width={24} height={24} />
            </Button>
            <Button
              variant={ButtonVariant.ICON}
              size={ButtonSizes.LG}
              onClick={resetAccessibility}
              className="banner-accessibility-panel__button"
              aria-label="Reset to default font size"
              data-testid="accessibility-panel-reset"
            >
              <Icon name="clear_all" width={24} height={24} />
            </Button>
          </div>

          <div className="banner-accessibility-panel__controls">
            <Button
              variant={ButtonVariant.GHOST}
              size={ButtonSizes.MD}
              onClick={turnOnAccessibilityPanel}
              className="banner-accessibility-panel__button banner-accessibility-panel__button--close"
              aria-label="Hide accessibility panel"
              data-testid="accessibility-panel-close"
            >
              <Icon className="banner-accessibility-panel__icon-close" name="close" width={24} height={24} />
              <Typography
                as="span"
                variant="TEXT16"
                className="banner-accessibility-panel__settings-title banner-accessibility-panel__settings-title--desktop"
              >
                Закрити налаштування
              </Typography>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
